import React, { useState, useEffect, useContext } from 'react';
import { Tour } from 'antd';
import {
  RocketOutlined,
  RobotOutlined,
  FileTextOutlined,
  InboxOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../../context/AuthContext';

/**
 * OnboardingTour Component
 * Location: frontend/src/components/common/OnboardingTour.js
 *
 * Interactive tour for first-time users to learn about Spark features.
 * Uses Ant Design Tour component with localStorage to track completion.
 * Only shows for authenticated users on protected routes.
 *
 * @param {boolean} forceShow - Force show tour even if already completed
 *
 * @example
 * <OnboardingTour />
 *
 * @example
 * // Force show tour for testing
 * <OnboardingTour forceShow={true} />
 */
const OnboardingTour = ({ forceShow = false }) => {
  const [tourOpen, setTourOpen] = useState(false);
  const { currentUser } = useContext(AuthContext);

  // Check if tour has been completed and user is authenticated
  useEffect(() => {
    // Only show tour if user is authenticated and not on login/auth pages
    const isAuthPage = ['/login', '/forgot-password', '/reset-password', '/register'].includes(window.location.pathname);

    if (!currentUser || isAuthPage) {
      return;
    }

    const hasCompletedTour = localStorage.getItem('spark-onboarding-completed');

    if (!hasCompletedTour || forceShow) {
      // Delay tour start to allow page to render and user to orient themselves
      const timer = setTimeout(() => {
        setTourOpen(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [forceShow, currentUser]);

  // Mark tour as completed
  const handleTourClose = () => {
    setTourOpen(false);
    localStorage.setItem('spark-onboarding-completed', 'true');
  };

  // Tour steps
  const tourSteps = [
    {
      title: (
        <span>
          <RocketOutlined style={{ marginRight: '8px' }} />
          Welcome to Spark!
        </span>
      ),
      description:
        "Let's take a quick tour of Spark's main features. This will only take 2 minutes.",
      target: null, // Centered modal
    },
    {
      title: (
        <span>
          <RobotOutlined style={{ marginRight: '8px' }} />
          AI Coach
        </span>
      ),
      description:
        'Get instant answers, feedback, and guidance. Ask questions, brainstorm ideas, or work through challenges with AI using voice or text input.',
      target: () => document.querySelector('[data-tour="ai-coach"]'),
      placement: 'right',
    },
    {
      title: (
        <span>
          <FileTextOutlined style={{ marginRight: '8px' }} />
          Report Writer
        </span>
      ),
      description:
        'Create professional reports with AI assistance. Upload documents, analyze content, and export in multiple formats.',
      target: () => document.querySelector('[data-tour="report-writer"]'),
      placement: 'right',
    },
    {
      title: (
        <span>
          <InboxOutlined style={{ marginRight: '8px' }} />
          Archive
        </span>
      ),
      description:
        'Automatically search your archived reports for relevant content when creating new reports. Simply load your old reports here and our AI will catalog them for insights on new reports.',
      target: () => document.querySelector('[data-tour="archive"]'),
      placement: 'right',
    },
    {
      title: (
        <span>
          <UserOutlined style={{ marginRight: '8px' }} />
          Your Account
        </span>
      ),
      description:
        "Manage your profile, view your organization's plan and token usage, change password, and customize settings.",
      target: () => document.querySelector('[data-tour="account-menu"]'),
      placement: 'bottomRight',
    },
    {
      title: 'Ready to Get Started!',
      description:
        "You're all set! Explore Spark at your own pace. Navigate using the sidebar menu, and use your account menu for settings and preferences.",
      target: null,
    },
  ];

  return (
    <Tour
      open={tourOpen}
      onClose={handleTourClose}
      steps={tourSteps}
      indicatorsRender={(current, total) => (
        <span
          style={{
            fontSize: '13px',
            color: '#8c8c8c',
          }}
        >
          {current + 1} / {total}
        </span>
      )}
      // Professional styling
      styles={{
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
        },
        modal: {
          borderRadius: '12px',
          boxShadow: '0 12px 48px rgba(0, 0, 0, 0.25)',
        },
        content: {
          fontSize: '15px',
          lineHeight: '1.6',
          color: '#3d3d3d',
        },
        header: {
          fontSize: '18px',
          fontWeight: 600,
          paddingBottom: '12px',
        },
        footer: {
          paddingTop: '16px',
        },
      }}
    />
  );
};

/**
 * Reset onboarding tour completion
 * Useful for testing or if user wants to see tour again
 */
export const resetOnboardingTour = () => {
  localStorage.removeItem('spark-onboarding-completed');
};

/**
 * Check if user has completed onboarding
 */
export const hasCompletedOnboarding = () => {
  return localStorage.getItem('spark-onboarding-completed') === 'true';
};

export default OnboardingTour;
